import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class ConfirmationDialogService {
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  /**
   * Mostra un dialog di conferma per tornare al registro
   * @param targetRoute Route di destinazione (default: /axa/suppliers-registry)
   */
  confirmNavigateToRegister(targetRoute: string = '/axa/suppliers-registry'): void {
    this.confirmationService.confirm({
      header: 'Conferma',
      message: 'Eventuali modifiche non salvate verranno perse. Vuoi procedere?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, procedi',
      rejectLabel: 'Annulla',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.router.navigate([targetRoute]);
      }
    });
  }

  /**
   * Mostra un dialog di conferma per annullare modifiche in corso
   * @param onAccept Callback da eseguire se confermato
   * @param onReject Callback opzionale da eseguire se annullato
   */
  confirmCancelEdit(onAccept: () => void, onReject?: () => void): void {
    this.confirmationService.confirm({
      header: 'Conferma',
      message: 'Eventuali modifiche non salvate verranno perse. Vuoi procedere?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, procedi',
      rejectLabel: 'Annulla',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: onAccept,
      reject: onReject
    });
  }

  /**
   * Mostra un dialog di conferma per eliminazione (pulsante rosso)
   * @param message Messaggio da visualizzare
   * @param onAccept Callback da eseguire se confermato
   * @param header Intestazione del dialog (default: 'Conferma Eliminazione')
   */
  confirmDelete(
    message: string,
    onAccept: () => void,
    header: string = 'Conferma Eliminazione'
  ): void {
    this.confirmationService.confirm({
      header,
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: onAccept
    });
  }

  /**
   * Mostra un dialog di conferma generico con pulsante blu standard
   * @param message Messaggio da visualizzare
   * @param onAccept Callback da eseguire se confermato
   * @param header Intestazione del dialog (default: 'Conferma')
   * @param acceptLabel Etichetta pulsante conferma (default: 'Sì, procedi')
   * @param rejectLabel Etichetta pulsante rifiuto (default: 'Annulla')
   */
  confirmAction(
    message: string,
    onAccept: () => void,
    header: string = 'Conferma',
    acceptLabel: string = 'Sì, procedi',
    rejectLabel: string = 'Annulla'
  ): void {
    this.confirmationService.confirm({
      header,
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel,
      rejectLabel,
      rejectButtonStyleClass: 'p-button-outlined',
      accept: onAccept
    });
  }

  /**
   * Mostra un dialog di conferma generico
   * @param options Opzioni di configurazione del dialog
   */
  confirm(options: {
    header: string;
    message: string;
    icon?: string;
    acceptLabel?: string;
    rejectLabel?: string;
    accept?: () => void;
    reject?: () => void;
  }): void {
    this.confirmationService.confirm({
      header: options.header,
      message: options.message,
      icon: options.icon || 'pi pi-exclamation-triangle',
      acceptLabel: options.acceptLabel || 'Conferma',
      rejectLabel: options.rejectLabel || 'Annulla',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: options.accept,
      reject: options.reject
    });
  }

  /**
   * Mostra un dialog informativo (solo con pulsante OK, nessuna azione richiesta)
   * @param message Messaggio da visualizzare
   * @param header Intestazione del dialog (default: 'Informazione')
   * @param icon Icona da mostrare (default: 'pi pi-info-circle')
   */
  showInfo(
    message: string,
    header: string = 'Informazione',
    icon: string = 'pi pi-info-circle'
  ): void {
    this.confirmationService.confirm({
      header,
      message,
      icon,
      acceptLabel: 'OK',
      rejectVisible: false,
      acceptButtonStyleClass: 'p-button-info'
    });
  }
}
